// src/pages/admin/notifications/Notifications.jsx

import React, { useState, useContext, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import {
  FiBell,
  FiCheck,
  FiTrash2,
  FiMail,
  FiAlertCircle,
  FiTool,
  FiDollarSign,
  FiPackage,
  FiShield,
  FiClock,
} from "react-icons/fi";
import Swal from "sweetalert2";
import axios from "axios";
import AppContext from "../../../context/AppContext.js";
import MasterLayout from "../../../layout/superadmin/MasterLayout";
import "../../../assets/css/superadmin/notifications/Notifications.css";
import { getEcho } from "../../../utils/echo";

// ─── Type Badge Config ────────────────────────────────────────────────────────
const TYPE_BADGE_MAP = {
  rental: { label: "Rental", bg: "#FFF8E6", color: "#FDB714" },
  maintenance: { label: "Maintenance", bg: "#FFE6E6", color: "#FF6B6B" },
  payment: { label: "Payment", bg: "#E6F9EE", color: "#51CF66" },
  equipment: { label: "Equipment", bg: "#FFF3E6", color: "#FF8C42" },
  admin: { label: "Admin", bg: "#EEF2FF", color: "#4C6EF5" },
};

const getTypeBadge = (type) =>
  TYPE_BADGE_MAP[type] || { label: type, bg: "#f0f0f0", color: "#666" };

// ─── Filter Tabs ──────────────────────────────────────────────────────────────
const FILTER_TABS = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
  { key: "read", label: "Read" },
];

// ─── Component ────────────────────────────────────────────────────────────────
const Notification = () => {
  const { baseUrl, loggedInAdmin ,authToken} = useContext(AppContext);

  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false); // ✅ FIX 1: loading state add kiya

  // ── PUSHER — is superadmin ke liye naya notification aate hi list
  //    ke upar add ho jayega, real-time ──
  useEffect(() => {
    if (!loggedInAdmin?.id) return;

    const echo = getEcho(baseUrl);
    if (!echo) return;

    const channel = echo.private(`superadmin.${loggedInAdmin.id}`);

    channel.listen(".new-notification", (e) => {
      setNotifications((prev) => [e.notification, ...prev]);
    });

    return () => {
      echo.leave(`superadmin.${loggedInAdmin.id}`);
    };
  }, [loggedInAdmin, baseUrl]);

  // ── Derived ────────────────────────────────────────────────────────────────
  // ✅ FIX 2: DB se status "unread" string aata hai — dono check karo
  const unreadCount = notifications.filter(
    (n) => n.status === "unread" || n.isRead === false
  ).length;
  const readCount = notifications.length - unreadCount;

  // ✅ FIX 3: filteredNotifications — status string bhi check karo
  const filteredNotifications = notifications.filter((n) => {
    const isUnread = n.status === "unread" || n.isRead === false;
    if (filter === "unread") return isUnread;
    if (filter === "read") return !isUnread;
    return true;
  });

  // ── API Handlers ─── ab yeh sab backend ko call karte hain, refresh
  // karne par purani state wapas nahi aati ──
  const markAllAsRead = () => {
    axios
      .post(
        `${baseUrl}/superadmin/notifications/markAllAsRead`,
        { srsuperadmin_id: loggedInAdmin.id },
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      .then((res) => {
        if (res.data.success) {
          setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true, status: "read" })));
        }
      })
      .catch((err) => console.log(err));
  };

  const markAsRead = (id) => {
    axios
      .post(
        `${baseUrl}/superadmin/notifications/markAsRead`,
        { id, srsuperadmin_id: loggedInAdmin.id },
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      .then((res) => {
        if (res.data.success) {
          setNotifications((prev) =>
            prev.map((n) => (n.id === id ? { ...n, isRead: true, status: "read" } : n))
          );
        }
      })
      .catch((err) => console.log(err));
  };

  const deleteNotification = (id) => {
    if (!window.confirm("Are you sure you want to delete this notification?")) return;
    axios
      .post(
        `${baseUrl}/superadmin/notifications/deleteNotification`,
        { id, srsuperadmin_id: loggedInAdmin.id },
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      .then((res) => {
        if (res.data.success) {
          setNotifications((prev) => prev.filter((n) => n.id !== id));
        }
      })
      .catch((err) => console.log(err));
  };

  const clearAllRead = () => {
    if (!window.confirm("Clear all read notifications?")) return;
    axios
      .post(
        `${baseUrl}/superadmin/notifications/clearRead`,
        { srsuperadmin_id: loggedInAdmin.id },
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      .then((res) => {
        if (res.data.success) {
          setNotifications((prev) => prev.filter((n) => n.status === "unread" || n.isRead === false));
        }
      })
      .catch((err) => console.log(err));
  };

  const viewAllNotifications = () => {
    if (!loggedInAdmin?.id) return; // 👈 guard against the race condition below
    setLoading(true);
    const formData = new FormData();
    formData.append("srsuperadmin_id", loggedInAdmin.id);
    
    axios
      .post(
        `${baseUrl}/superadmin/notifications/viewAllNotifications`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${authToken}`,
          },
        }
      )
      .then((response) => {
        setLoading(false);
        if (response.data.hasOwnProperty("error")) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setNotifications(response.data.notifications);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load notifications.",
        });
      });
  };

  // 👇 loggedInAdmin MasterLayout me localStorage se async load hota hai —
  // is liye jab tak wo ready na ho, request fire hi nahi karni chahiye.
  // Warna loggedInAdmin.id crash kar jata hai (Cannot read 'id' of undefined).
  useEffect(() => {
    if (loggedInAdmin?.id) {
      viewAllNotifications();
    }
  }, [loggedInAdmin]);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <MasterLayout>
      <div className="an-page">
        <Container fluid>
          {/* ── Header ── */}
          <div className="an-header">
            <div className="an-header-left">
              <h2 className="an-title">Notifications</h2>
              {unreadCount > 0 && (
                <span className="an-unread-badge">{unreadCount} New</span>
              )}
            </div>
            <div className="an-header-actions">
              <button
                className="an-mark-all-btn"
                onClick={markAllAsRead}
                disabled={unreadCount === 0}
              >
                <FiMail size={15} />
                Mark All as Read
              </button>
              <button className="an-clear-all-btn" onClick={clearAllRead}>
                Clear Read
              </button>
            </div>
          </div>

          {/* ── Filter Tabs ── */}
          <div className="an-filter-section">
            <div className="an-filter-buttons">
              {FILTER_TABS.map((tab) => {
                const count =
                  tab.key === "all"
                    ? notifications.length
                    : tab.key === "unread"
                    ? unreadCount
                    : readCount;
                return (
                  <button
                    key={tab.key}
                    className={`an-filter-btn ${filter === tab.key ? "active" : ""}`} // ✅ className restore kiya
                    onClick={() => setFilter(tab.key)}
                  >
                    {tab.label} ({count})
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Loading State ── */}
          {loading && (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <p>Loading notifications...</p>
            </div>
          )}

          {/* ── List ── */}
          {!loading && (
            <Row>
              <Col>
                <div className="an-list">
                  {filteredNotifications.length > 0 ? ( // ✅ filteredNotifications use karo
                    filteredNotifications.map((notification) => {
                      const typeBadge = getTypeBadge(notification.type);
                      const isUnread =
                        notification.status === "unread" ||
                        notification.isRead === false;
                      return (
                        <div
                          key={notification.id}
                          className={`an-card ${isUnread ? "an-unread" : ""}`}
                        >
                          <div className="an-card-body">
                            <div className="an-card-content">
                              {/* ── Details ── */}
                              <div className="an-details">
                                <div className="an-title-row">
                                  <h5 className="an-card-title">
                                    {notification.title}
                                  </h5>
                                  <div className="an-badges">
                                    <span
                                      className="an-type-badge"
                                      style={{
                                        background: typeBadge.bg,
                                        color: typeBadge.color,
                                      }}
                                    >
                                      {typeBadge.label}
                                    </span>
                                    {isUnread && (
                                      <span className="an-new-badge">New</span>
                                    )}
                                  </div>
                                </div>

                                <p className="an-message">
                                  {notification.message}
                                </p>

                                <span className="an-time">
                                  <FiClock size={11} />
                                  {notification.time || notification.created_at}
                                </span>
                              </div>

                              {/* ── Actions ── */}
                              <div className="an-action-btns">
                                {isUnread && (
                                  <button
                                    className="an-action-btn an-read-btn"
                                    onClick={() => markAsRead(notification.id)}
                                    title="Mark as read"
                                  >
                                    <FiCheck size={15} />
                                  </button>
                                )}
                                <button
                                  className="an-action-btn an-delete-btn"
                                  onClick={() =>
                                    deleteNotification(notification.id)
                                  }
                                  title="Delete"
                                >
                                  <FiTrash2 size={15} />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="an-empty">
                      <FiBell className="an-empty-icon" />
                      <h4>No Notifications</h4>
                      <p>You're all caught up!</p>
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          )}
        </Container>
      </div>
    </MasterLayout>
  );
};

export default Notification;